import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useBranding } from '../../context/BrandingContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Separator } from '../ui/separator';
import { Globe, Building, Upload, Image as ImageIcon, Palette, Loader2 } from 'lucide-react';

const PRESET_COLORS = [
  { hex: '#6366F1', name: 'Indaco' },
  { hex: '#3B82F6', name: 'Blu' },
  { hex: '#06B6D4', name: 'Ciano' },
  { hex: '#10B981', name: 'Smeraldo' },
  { hex: '#F59E0B', name: 'Ambra' },
  { hex: '#EF4444', name: 'Rosso' },
  { hex: '#EC4899', name: 'Rosa' },
  { hex: '#8B5CF6', name: 'Viola' },
  { hex: '#F97316', name: 'Arancione' },
  { hex: '#14B8A6', name: 'Teal' },
  { hex: '#0EA5E9', name: 'Celeste' },
  { hex: '#A855F7', name: 'Porpora' },
];

const PlatformTab = ({ initialData }) => {
  const { API, getAuthHeaders } = useAuth();
  const { refreshBranding, applyColor } = useBranding();
  const [branding, setBranding] = useState({
    brand_name: initialData?.brand_name || '',
    logo_url: initialData?.logo_url || '',
    favicon_url: initialData?.favicon_url || '',
    primary_color: initialData?.primary_color || '',
    support_whatsapp: initialData?.support_whatsapp || ''
  });
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);

  const saveBranding = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/platform-branding`, {
        brand_name: branding.brand_name || null,
        logo_url: branding.logo_url || null,
        favicon_url: branding.favicon_url || null,
        primary_color: branding.primary_color || null,
        support_whatsapp: branding.support_whatsapp || null
      }, getAuthHeaders());
      refreshBranding();
      alert('Branding piattaforma salvato con successo!');
    } catch (error) {
      console.error('Errore salvataggio branding:', error);
      alert(error.response?.data?.detail || 'Errore nel salvataggio del branding');
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/platform-branding/logo`, formData, {
        ...getAuthHeaders(),
        headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' }
      });
      setBranding(prev => ({ ...prev, logo_url: response.data.logo_url + '?t=' + Date.now() }));
      alert('Logo piattaforma caricato con successo!');
    } catch (error) {
      console.error('Errore upload logo piattaforma:', error);
      alert(error.response?.data?.detail || 'Errore nel caricamento del logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const uploadFavicon = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingFavicon(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/platform-branding/favicon`, formData, {
        ...getAuthHeaders(),
        headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' }
      });
      const newUrl = response.data.favicon_url + '?t=' + Date.now();
      setBranding(prev => ({ ...prev, favicon_url: newUrl }));
      // Apply favicon immediately
      const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
      link.rel = 'icon';
      link.href = `${API}${response.data.favicon_url}`;
      document.head.appendChild(link);
      alert('Favicon caricata con successo!');
    } catch (error) {
      console.error('Errore upload favicon:', error);
      alert(error.response?.data?.detail || 'Errore nel caricamento della favicon');
    } finally {
      setUploadingFavicon(false);
    }
  };

  const resetColor = () => {
    setBranding(prev => ({ ...prev, primary_color: '' }));
    const root = document.documentElement;
    root.style.removeProperty('--primary');
    root.style.removeProperty('--primary-foreground');
    root.style.removeProperty('--accent');
    root.style.removeProperty('--accent-foreground');
    root.style.removeProperty('--ring');
    root.style.removeProperty('--chart-1');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-outfit flex items-center gap-2">
          <Globe className="w-5 h-5" />
          Branding Piattaforma
        </CardTitle>
        <CardDescription>
          Configura il logo e il nome che saranno visibili a tutti gli utenti della piattaforma,
          sia nella pagina di login che nella sidebar dopo l'accesso.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Platform Logo */}
        <div className="space-y-3">
          <Label>Logo Piattaforma</Label>
          <div className="flex items-center gap-4">
            <div className="w-24 h-24 rounded-lg border-2 border-dashed border-muted-foreground/30 flex items-center justify-center overflow-hidden bg-muted/30">
              {branding.logo_url ? (
                <img src={`${API}${branding.logo_url}`} alt="Logo Piattaforma" className="w-full h-full object-contain" />
              ) : (
                <ImageIcon className="w-8 h-8 text-muted-foreground/50" />
              )}
            </div>
            <div className="space-y-2">
              <input type="file" accept="image/*" onChange={uploadLogo} className="hidden" id="platform-logo-upload" />
              <Button variant="outline" size="sm" onClick={() => document.getElementById('platform-logo-upload').click()} disabled={uploadingLogo}>
                {uploadingLogo ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Carica Logo
              </Button>
              <p className="text-xs text-muted-foreground">JPG, PNG o WebP. Max 5MB. Consigliato: logo su sfondo trasparente.</p>
            </div>
          </div>
        </div>

        {/* Favicon */}
        <div className="space-y-3">
          <Label>Icona App (Favicon)</Label>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-lg border-2 border-dashed border-muted-foreground/30 flex items-center justify-center overflow-hidden bg-muted/30">
              {branding.favicon_url ? (
                <img src={`${API}${branding.favicon_url}`} alt="Favicon" className="w-full h-full object-contain" />
              ) : (
                <Globe className="w-6 h-6 text-muted-foreground/50" />
              )}
            </div>
            <div className="space-y-2">
              <input type="file" accept="image/png,image/x-icon,image/svg+xml,image/jpeg,image/webp,.ico" onChange={uploadFavicon} className="hidden" id="platform-favicon-upload" />
              <Button variant="outline" size="sm" onClick={() => document.getElementById('platform-favicon-upload').click()} disabled={uploadingFavicon} data-testid="upload-favicon-btn">
                {uploadingFavicon ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Carica Favicon
              </Button>
              <p className="text-xs text-muted-foreground">PNG, ICO o SVG. Max 2MB. Consigliato: 32x32 o 64x64px.</p>
            </div>
          </div>
        </div>

        <Separator />

        {/* Brand Name */}
        <div className="space-y-2">
          <Label htmlFor="platform_brand_name">Nome del Brand</Label>
          <Input id="platform_brand_name" value={branding.brand_name} onChange={(e) => setBranding(prev => ({ ...prev, brand_name: e.target.value }))} placeholder="Es. Beauty Management Platform" />
          <p className="text-xs text-muted-foreground">Questo nome verrà mostrato accanto al logo nella pagina di login e nella sidebar.</p>
        </div>

        <Separator />

        {/* Primary Color Picker */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Palette className="w-4 h-4 text-muted-foreground" />
            <Label>Colore Primario</Label>
          </div>
          <p className="text-sm text-muted-foreground">Questo colore verrà applicato a pulsanti, sidebar, link e tutti gli elementi principali dell'interfaccia.</p>

          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Colori predefiniti</Label>
            <div className="flex flex-wrap gap-2">
              {PRESET_COLORS.map((color) => (
                <button key={color.hex} type="button" title={color.name}
                  className={`w-9 h-9 rounded-lg transition-all border-2 hover:scale-110 ${branding.primary_color?.toUpperCase() === color.hex.toUpperCase() ? 'border-foreground ring-2 ring-offset-2 ring-foreground/30 scale-110' : 'border-transparent'}`}
                  style={{ backgroundColor: color.hex }}
                  onClick={() => { setBranding(prev => ({ ...prev, primary_color: color.hex })); applyColor(color.hex); }}
                  data-testid={`preset-color-${color.name.toLowerCase()}`}
                />
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <input type="color" value={branding.primary_color || '#6366F1'}
              onChange={(e) => { setBranding(prev => ({ ...prev, primary_color: e.target.value })); applyColor(e.target.value); }}
              className="w-12 h-10 rounded-lg cursor-pointer border border-border bg-transparent p-0.5"
              data-testid="custom-color-picker"
            />
            <div className="flex-1">
              <Input value={branding.primary_color || ''}
                onChange={(e) => {
                  let val = e.target.value;
                  if (val && !val.startsWith('#')) val = '#' + val;
                  setBranding(prev => ({ ...prev, primary_color: val }));
                  if (/^#[0-9A-Fa-f]{6}$/.test(val)) applyColor(val);
                }}
                placeholder="#6366F1" maxLength={7} className="font-mono" data-testid="hex-color-input"
              />
            </div>
            {branding.primary_color && (
              <Button variant="ghost" size="sm" onClick={resetColor} className="text-xs" data-testid="reset-color-btn">Reset</Button>
            )}
          </div>
        </div>

        {/* Preview */}
        <div className="p-4 rounded-lg bg-muted/50 space-y-3">
          <Label className="text-sm font-medium">Anteprima</Label>
          <div className="flex items-center gap-3 p-3 bg-background rounded-lg border">
            {branding.logo_url ? (
              <img src={`${API}${branding.logo_url}`} alt="Preview" className="h-10 w-auto object-contain" />
            ) : (
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: branding.primary_color || 'hsl(239, 84%, 67%)' }}>
                <Building className="w-5 h-5 text-white" />
              </div>
            )}
            <span className="font-semibold font-outfit">{branding.brand_name || 'Nome Brand'}</span>
          </div>
          <div className="flex items-center gap-3">
            <Button size="sm" data-testid="preview-primary-btn">Pulsante Primario</Button>
            <Button size="sm" variant="outline">Pulsante Outline</Button>
          </div>
        </div>

        <Separator />

        {/* Support WhatsApp */}
        <div className="space-y-3">
          <Label>Numero WhatsApp Assistenza</Label>
          <Input
            value={branding.support_whatsapp}
            onChange={(e) => setBranding(prev => ({ ...prev, support_whatsapp: e.target.value }))}
            placeholder="+39 333 1234567"
            data-testid="support-whatsapp-input"
          />
          <p className="text-xs text-muted-foreground">Questo numero verrà mostrato nella sezione Assistenza per contatto diretto WhatsApp</p>
        </div>

        <div className="flex justify-end pt-4">
          <Button onClick={saveBranding} disabled={saving}>
            {saving ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Salvataggio...</>) : 'Salva Branding Piattaforma'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default PlatformTab;
